import type { ReactNode } from 'react';
import React, { useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { canPerformSpaceOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';
import { ImportOnboardingCTA } from '@confluence/experiment-twcg-2304-import-in-onboarding/entry-points/ImportOnboardingCTA.ts';
import type { ImportSource } from '@confluence/experiment-twcg-2304-import-in-onboarding/entry-points/ImportOnboardingCTA.ts';
import { OnboardingImportLauncher } from '@confluence/import-from-sources/entry-points/OnboardingImportLauncher.ts';

import { useSpaceCreationEligibility } from '../SpaceCreationEligibility.tsx';

import type {
	FooterCreateButtonsQuery$data,
	FooterCreateButtonsQuery$variables,
} from './__generated__/FooterCreateButtonsQuery.graphql.ts';

import type { FooterCreateButtons_space$key } from './__generated__/FooterCreateButtons_space.graphql.ts';

const FooterCreateButtonsSpaceFragment = graphql`
	fragment FooterCreateButtons_space on Space {
		id
		key
		spaceTypeSettings {
			enabledContentTypes {
				isLivePagesEnabled
				isWhiteboardsEnabled
				isDatabasesEnabled
			}
		}
		didContainUserContent
		operations {
			operation
			targetType
		}
	}
`;

const FooterCreateButtonsQuery = graphql`
	query FooterCreateButtonsQuery($spaceKey: String) {
		space(key: $spaceKey) {
			...FooterCreateButtons_space @relay(mask: false)
		}
	}
`;

const ContentTreeOnboardingLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ContentTreeOnboarding" */ './ContentTreeOnboarding.tsx'
			)
		).ContentTreeOnboarding,
});

const TreeBottomCreateButtonLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-TreeBottomCreateButton" */ './TreeBottomCreateButton.tsx'
			)
		).TreeBottomCreateButton,
});

type FooterCreateButtonsProps = {
	spaceKey: string;
	isContentTreeLoading: boolean;
	emptyContent?: ReactNode;
};

export const FooterCreateButtons = (props: FooterCreateButtonsProps): React.JSX.Element => {
	const space = useSpaceCreationEligibility(props.spaceKey);
	return space ? (
		<FragmentFooterCreateButtons {...props} spaceFragmentRef={space} />
	) : (
		<LegacyFooterCreateButtons {...props} />
	);
};

const FragmentFooterCreateButtons = ({
	spaceFragmentRef,
	...props
}: FooterCreateButtonsProps & {
	spaceFragmentRef: FooterCreateButtons_space$key;
}): React.JSX.Element => {
	const space = useFragment(FooterCreateButtonsSpaceFragment, spaceFragmentRef);
	return <FooterCreateButtonsContent {...props} data={{ space }} loading={false} />;
};

const LegacyFooterCreateButtons = (props: FooterCreateButtonsProps): React.JSX.Element => {
	const { spaceKey } = props;
	const { data, loading, error } = useQueryInterop<
		FooterCreateButtonsQuery$data,
		FooterCreateButtonsQuery$variables
	>(FooterCreateButtonsQuery, {
		errorPolicy: 'all',
		variables: {
			spaceKey,
		},
	});
	return <FooterCreateButtonsContent {...props} data={data} loading={loading} error={error} />;
};

const FooterCreateButtonsContent = ({
	spaceKey,
	isContentTreeLoading,
	emptyContent,
	data,
	loading,
	error,
}: FooterCreateButtonsProps & {
	data?: FooterCreateButtonsQuery$data;
	loading: boolean;
	error?: Error;
}): React.JSX.Element | null => {
	const isDatabasesGloballyEnabled = isContentTypeEnabledInCurrentEnv('database');
	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireImportCTAAnalytics = useCallback(
		(source: ImportSource) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: source,
					source: 'twcg-2304-import-in-onboarding-import-button',
					attributes: {
						importSource: source,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	if (isContentTreeLoading || loading || !data) {
		return null;
	}

	const canCreatePage = canPerformSpaceOperation({
		operationCheckResult: data,
		operation: OperationTypes.CREATE,
		contentType: 'page',
	});

	if (!canCreatePage) {
		return <>{emptyContent}</>;
	}

	const didContainUserContent = data?.space?.didContainUserContent;

	const showContentTreeOnboarding = didContainUserContent === false;
	const showLiveDoc = Boolean(
		data?.space?.spaceTypeSettings?.enabledContentTypes?.isLivePagesEnabled,
	);
	const showWhiteboard =
		isWhiteboardFeatureEnabled('whiteboardsEnabled') &&
		Boolean(data?.space?.spaceTypeSettings?.enabledContentTypes?.isWhiteboardsEnabled);
	const showDatabase =
		isDatabasesGloballyEnabled &&
		Boolean(data?.space?.spaceTypeSettings?.enabledContentTypes?.isDatabasesEnabled);

	return (
		<>
			{showContentTreeOnboarding ? (
				<ContentTreeOnboardingLoader
					showLiveDoc={showLiveDoc}
					showWhiteboard={showWhiteboard}
					showDatabase={showDatabase}
				/>
			) : (
				emptyContent || (
					<>
						<TreeBottomCreateButtonLoader
							spaceKey={spaceKey}
							source="endOfContentTreeContextualCreate"
						/>
						{/*
						 * The launcher runs the full import flow in place — auth, file picker,
						 * progress and error modals — so selecting a source launches the auth.
						 * The user is taken to the imported content only
						 * once the import succeeds.
						 */}
						<OnboardingImportLauncher spaceKey={spaceKey}>
							{({ startImport }) => (
								<ImportOnboardingCTA
									onSelectSource={(source) => {
										fireImportCTAAnalytics(source);
										startImport(source);
									}}
								/>
							)}
						</OnboardingImportLauncher>
					</>
				)
			)}
			{error && <ErrorDisplay error={error} />}
		</>
	);
};
